<template>
  <BaseModal
    v-model:isOpen="isOpen"
    :show-default-footer="false"
    :close-on-overlay-click="false"
    showCloseButton
    content-padding="none"
    size="md"
    title="개인정보 수정"
    @close="handleClose"
  >
    <div class="px-padding-36 py-10">
      <div class="flex flex-col gap-8">
        <LabelContent
          class="pb-padding-16 border-bg-bg-outline border-b"
          label="이름"
          size="custom"
          :style="'text-font-14 text-default-muted'"
        >
          <template #content>
            <div class="mt-size-4">
              <div class="flex items-center justify-between">
                <span class="text-font-16 text-default-muted font-medium">
                  {{ memberInfo.name }}
                </span>
              </div>
            </div>
          </template>
        </LabelContent>
        <LabelContent
          class="pb-padding-16 border-bg-bg-outline border-b"
          label="이메일"
          size="custom"
          :style="'text-font-14 text-default-muted'"
        >
          <template #content>
            <div class="mt-size-4">
              <div class="flex items-center justify-between">
                <span class="text-font-16 text-default-muted font-medium">{{
                  memberInfo.email
                }}</span>
              </div>
            </div>
          </template>
        </LabelContent>
        <LabelContent
          class="pb-padding-16 border-bg-bg-outline border-b"
          label="비밀번호"
          size="custom"
          :style="'text-font-14 text-default-muted'"
        >
          <template #content>
            <div class="mt-size-4">
              <div class="flex items-center justify-between">
                <span class="text-font-16 font-medium">●●●●●●●●●●●●</span>
                <BaseButton
                  variant="chip"
                  color="grey"
                  label="변경하기"
                  @click="handleOpenPasswordReset"
                />
              </div>
            </div>
          </template>
        </LabelContent>
        <LabelContent
          class="pb-padding-16 border-bg-bg-outline border-b"
          label="휴대폰번호"
          size="custom"
          :style="'text-font-14 text-default-muted'"
        >
          <template #content>
            <div class="mt-size-4">
              <div class="flex items-center justify-between">
                <span class="text-font-16 font-medium">{{ memberInfo.phoneNo }} </span>
                <!-- TODO: PASS 인증 버튼 클릭 시 휴대폰 본인인증 팝업 띄우기 -->
                <BaseButton
                  variant="chip"
                  color="grey"
                  label="변경하기"
                  @click="handleVerifyPhone"
                />
              </div>
            </div>
          </template>
        </LabelContent>
        <LabelContent
          class="pb-padding-16"
          label="거주지 주소"
          size="custom"
          :style="'text-font-14 text-default-muted'"
        >
          <template #content>
            <div class="mt-size-4">
              <div v-if="!inputState.updateAddress" class="flex items-center justify-between">
                <span class="text-font-16 font-medium"
                  >{{ memberInfo.address }} <br />{{ memberInfo.detailAddress }}</span
                >
                <BaseButton
                  v-if="!inputState.updateAddress"
                  variant="chip"
                  color="grey"
                  label="변경하기"
                  @click="inputState.updateAddress = true"
                />
              </div>
              <div v-else class="gap-size-8 flex flex-col">
                <BaseInput
                  size="md"
                  placeholder="주소 검색"
                  v-model="memberInfo.address"
                  variant="search"
                  @onSearch="openDaumPostcode"
                  @click="openDaumPostcode"
                  readonly
                  full
                />

                <BaseInput
                  size="md"
                  placeholder="상세주소"
                  v-model="memberInfo.detailAddress"
                  full
                  allowSpaces
                />
                <div class="gap-size-8 flex items-center justify-between">
                  <!-- TODO: 저장 버튼 클릭 시 거주지 주소 수정 완료 후 저장 API 호출-->
                  <BaseButton
                    label="저장"
                    variant="contained"
                    color="primary"
                    :disabled="!memberInfo.address || !memberInfo.detailAddress"
                    @click="handleUpdateAddress"
                    fullWidth
                  />
                  <BaseButton
                    label="취소"
                    variant="contained-grey"
                    color="grey"
                    @click="inputState.updateAddress = false"
                    fullWidth
                  />
                </div>
              </div>
            </div>
          </template>
        </LabelContent>
      </div>
      <div class="mt-size-36">
        <BaseButton variant="outlined" color="black" label="닫기" @click="handleClose" fullWidth />
      </div>
    </div>
  </BaseModal>
</template>
<script setup lang="ts">
import type { MemberInfoResponse, KcbPhoneVerifyResultRequestMemberType } from '@template/api';
import { useGlobalDialogManager } from '@/composables/useGlobalDialogManager';
import { usePhoneVerification } from '@/composables/usePhoneVerification';
import { LocalStorageService, LocalStorageKey } from '@template/utils';
import LabelContent from '@/components/common/LabelContent.vue';
import { BaseModal, BaseButton, BaseInput } from '@template/ui';
import { useToastStore } from '@/stores/useToastStore';
import { useUserStore } from '@/stores/useUserStore';
import { TOAST_MESSAGES } from '@template/types';
import { reactive, onMounted, watch } from 'vue';
import { memberService } from '@/services/api';

interface Emits {
  (e: 'close'): void;
}

const toastStore = useToastStore();
const userStore = useUserStore();

const emit = defineEmits<Emits>();

const isOpen = defineModel<boolean>('isOpen', { required: true });

const dialogManager = useGlobalDialogManager();

const { verifyPhone } = usePhoneVerification({
  onSuccess: async (result) => {
    try {
      await memberService.updatePhone({
        ci: result.ci,
        phoneNo: result.phoneNo,
      });

      toastStore.addToast(TOAST_MESSAGES.user.personal_info_changed);
    } catch (error) {
      console.error('휴대폰 변경 본인인증 처리 중 오류:', error);
    }
  },
  onError: (error) => {
    console.error('휴대폰 변경 본인인증 처리 중 오류:', error);
  },
});

/**
 * 비밀번호 재설정 다이얼로그 열기
 */
const handleOpenPasswordReset = () => {
  dialogManager.open('resetPassword');
};

const handleVerifyPhone = () => {
  const memberType = LocalStorageService.getItem(LocalStorageKey.MEMBER_TYPE);
  const verifiedMemberType: KcbPhoneVerifyResultRequestMemberType =
    memberType === 'INDIVIDUAL' || memberType === 'CORPORATE' ? memberType : 'INDIVIDUAL';
  verifyPhone('02', verifiedMemberType);
};

const memberInfo = reactive<MemberInfoResponse>({
  name: '',
  email: '',
  address: '',
  detailAddress: '',
  phoneNo: '',
  lastAccessTime: '',
  advertisingAgreed: false,
  kycStatus: 'NOT_SUBMITTED',
});

const inputState = reactive({
  updateAddress: false,
});

/**
 * 회원 정보 조회
 * 다이얼로그가 열릴 때마다 최신 정보를 가져옵니다.
 * 토큰이 만료된 경우 자동으로 갱신 후 재시도됩니다.
 */
const getMemberInfo = async () => {
  try {
    const response = await memberService.getInfo();

    memberInfo.name = response!.data!.name;
    memberInfo.phoneNo = response!.data!.phoneNo;
    memberInfo.lastAccessTime = response!.data!.lastAccessTime;
    memberInfo.email = response!.data!.email;
    memberInfo.address = response!.data!.address;
    memberInfo.detailAddress = response!.data!.detailAddress;
    memberInfo.kycStatus = response!.data!.kycStatus;
    userStore.updateUserEmail(response!.data!.email);
  } catch (error) {
    console.error('회원 정보 조회 실패:', error);
  }
};

const handleClose = () => {
  isOpen.value = false;
  emit('close');
};

// 주소 검색 열기 (새 창 방식)
const openDaumPostcode = () => {
  if (!window.daum?.Postcode) return;

  new window.daum.Postcode({
    oncomplete: (data: any) => {
      memberInfo.address = data.address;
      memberInfo.detailAddress = '';
    },
    onclose: () => {
      // 새 창이 닫힐 때의 처리 (필요시)
    },
    width: '100%',
    height: '100%',
  }).open();
};

const handleUpdateAddress = async () => {
  try {
    await memberService.updateAddress({
      address: memberInfo.address,
      detailAddress: memberInfo.detailAddress,
    });

    toastStore.addToast(TOAST_MESSAGES.user.personal_info_changed);
  } catch (error) {
    console.error(error);
  } finally {
    inputState.updateAddress = false;
  }
};

// 다이얼로그가 열릴 때마다 회원 정보 조회
watch(
  isOpen,
  (newValue) => {
    if (newValue) {
      getMemberInfo();
    }
  },
  { immediate: true }
);

onMounted(() => {
  const script = document.createElement('script');
  script.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
  document.head.appendChild(script);
});
</script>
